// Page Favoris - Entre Particuliers
// Accessible sans être connecté (favoris stockés en localStorage)
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart, ArrowLeft, Trash2, MapPin, Clock, Eye, MessageCircle,
  Package, X, ShoppingBag, Search, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import { MOCK_ANNONCES } from './AnnonceDetailPage';

export default function FavorisAnnoncesPage() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      // Récupérer les IDs favoris du localStorage
      const storedFavorites = JSON.parse(localStorage.getItem('annonces_favoris') || '[]');
      
      if (storedFavorites.length === 0) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      // Filtrer les annonces de test qui sont dans les favoris
      const favoriteAnnonces = MOCK_ANNONCES.filter(
        annonce => storedFavorites.includes(annonce.id)
      );
      setFavorites(favoriteAnnonces);
    } catch (error) {
      console.error('Erreur chargement favoris:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = (annonceId) => {
    // Retirer du localStorage
    const storedFavorites = JSON.parse(localStorage.getItem('annonces_favoris') || '[]');
    const newFavorites = storedFavorites.filter(id => id !== annonceId);
    localStorage.setItem('annonces_favoris', JSON.stringify(newFavorites));
    
    // Mettre à jour l'état
    setFavorites(favorites.filter(f => f.id !== annonceId));
    toast.success('Retiré des favoris');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <UnifiedHeader />
      <MainNavigation />
      <div className="h-24 md:hidden" />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Heart size={22} className="text-[#EC4899]" fill="#EC4899" />
                Mes favoris
              </h1>
              <p className="text-sm text-gray-500">
                {favorites.length} annonce{favorites.length !== 1 ? 's' : ''} sauvegardée{favorites.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#EC4899] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-200 p-12 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-pink-50 flex items-center justify-center mx-auto mb-6">
              <Heart size={40} className="text-pink-300" />
            </div>
            <h3 className="font-bold text-xl text-gray-900 mb-2">Aucun favori</h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              Vous n'avez pas encore d'annonces favorites. Parcourez les annonces et cliquez sur le cœur pour les sauvegarder.
            </p>
            <Link to="/particuliers">
              <Button className="bg-[#F97316] hover:bg-[#EA580C] rounded-xl font-semibold">
                <Search size={18} className="mr-2" />
                Parcourir les annonces
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {favorites.map((annonce, index) => (
              <motion.div
                key={annonce.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="flex">
                  {/* Image */}
                  <div className="relative w-32 sm:w-40 h-32 sm:h-40 flex-shrink-0 bg-gray-100">
                    {annonce.photos && annonce.photos.length > 0 ? (
                      <img
                        src={annonce.photos[0]}
                        alt={annonce.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={32} className="text-gray-300" />
                      </div>
                    )}
                    {/* Badge TEST */}
                    {annonce.id?.startsWith('test-') && (
                      <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1 shadow-md">
                        <Info size={10} />
                        TEST
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 flex flex-col">
                    <div className="flex-1">
                      <Link 
                        to={`/annonces/${annonce.id}`}
                        className="font-semibold text-gray-900 hover:text-[#1E40AF] transition-colors line-clamp-2"
                      >
                        {annonce.title}
                      </Link>
                      
                      <p className="text-[#F97316] font-bold text-lg mt-1">
                        {formatPrice(annonce.price)}
                        {annonce.price_negotiable && (
                          <span className="text-xs text-gray-500 font-normal ml-2">Négociable</span>
                        )}
                      </p>
                      
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={12} />
                          {annonce.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {formatTimeAgo(annonce.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <Link to={`/annonces/${annonce.id}`}>
                        <Button size="sm" className="bg-[#1E40AF] hover:bg-[#1E3A8A] rounded-lg text-xs h-8">
                          <Eye size={14} className="mr-1" />
                          Voir
                        </Button>
                      </Link>
                      
                      <button
                        onClick={() => removeFavorite(annonce.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-xs font-medium"
                      >
                        <Trash2 size={14} />
                        Retirer
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Info box pour inciter à s'inscrire */}
      {favorites.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <div className="bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] rounded-xl p-4 text-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Heart size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">Synchronise tes favoris</p>
                <p className="text-blue-100 text-sm mt-1">
                  Crée un compte pour retrouver tes favoris sur tous tes appareils et ne jamais les perdre.
                </p>
              </div>
              <Link to="/inscription">
                <Button size="sm" className="bg-white text-[#1E40AF] hover:bg-blue-50 rounded-lg font-semibold">
                  S'inscrire
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
